/**
 * ChestGateBonusIndicator Component
 *
 * Displays a hoverable "2x POINTS" indicator on the Chest page for Gate wallet users
 * Shows campaign details on hover including:
 * - Time remaining
 * - Points progress (X/20,000)
 * - Eligibility status
 *
 * Uses Gate's official branding (blue/green gradient).
 * Positioned above the chest slider bar, left-aligned
 */

import { useState } from 'react';

import config from '@/config';
import { useGateCampaign } from '@/hooks/useGateCampaign';

// Gate brand colors
const GATE_GREEN = '#17E6A1';
const GATE_BLUE = '#2354E6';

function formatTimeRemaining(endDate: string): string {
  const now = new Date();
  const end = new Date(endDate);
  const diff = end.getTime() - now.getTime();

  if (diff <= 0) {
    return 'Campaign ended';
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) {
    return `${days}d ${hours}h left`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m left`;
  }
  return `${minutes}m left`;
}

export function ChestGateBonusIndicator() {
  const { campaignData, isLoading } = useGateCampaign();
  const [isHovered, setIsHovered] = useState(false);

  // Feature flag check
  if (!config.ENABLE_GATE_WALLET_CAMPAIGN) {
    return null;
  }

  // Only show if user is using Gate wallet and hasn't reached the cap
  if (isLoading || !campaignData?.isActive || !campaignData?.isEligible || campaignData?.remainingBonus <= 0) {
    return null;
  }

  const isEligible = campaignData.isEligible && campaignData.remainingBonus > 0;
  const timeRemaining = formatTimeRemaining(campaignData.endDate);
  const progress = `${campaignData.userBonusEarned.toLocaleString()}/${campaignData.maxBonus.toLocaleString()}`;
  const progressPercent = (campaignData.userBonusEarned / campaignData.maxBonus) * 100;

  return (
    <div
      className="relative inline-flex"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Gate 2x Badge */}
      <div
        className="cursor-pointer transition-all duration-200 flex items-center gap-2 px-3 py-1.5 rounded-full"
        style={{
          transform: isHovered ? 'scale(1.05)' : 'scale(1)',
          background: 'rgba(240, 240, 240, 0.95)',
          border: `2px solid ${GATE_GREEN}`,
          boxShadow: `0 0 12px rgba(23, 230, 161, 0.4), 0 0 8px rgba(35, 84, 230, 0.3)`,
        }}
      >
        <img src="/gate-color-short.png" alt="Gate" className="h-4" />
        <span
          className="text-sm font-bold"
          style={{
            background: `linear-gradient(90deg, ${GATE_BLUE}, ${GATE_GREEN})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontFamily: 'Outfit, sans-serif',
          }}
        >
          2x
        </span>
      </div>

      {/* Hover Tooltip */}
      {isHovered && (
        <div
          className="absolute top-full left-0 mt-2 w-72 p-4 rounded-xl shadow-2xl z-50 animate-slide-in"
          style={{
            backgroundColor: '#343434',
            border: `2px solid transparent`,
            borderImage: `linear-gradient(135deg, ${GATE_BLUE}, ${GATE_GREEN}) 1`,
            boxShadow: `0 0 20px rgba(23, 230, 161, 0.3), 0 0 12px rgba(35, 84, 230, 0.2)`,
          }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 mb-3">
            <div className="rounded-lg px-2 py-1" style={{ background: 'rgba(240, 240, 240, 0.95)' }}>
              <img src="/gate-color-short.png" alt="Gate" className="h-4" />
            </div>
            <h3
              className="text-base font-bold"
              style={{
                background: `linear-gradient(90deg, ${GATE_BLUE}, ${GATE_GREEN})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontFamily: 'Outfit, sans-serif',
              }}
            >
              2x Bonus Active
            </h3>
          </div>

          {/* Content */}
          <div className="space-y-3">
            {isEligible ? (
              <>
                {/* For eligible users */}
                <p className="text-sm" style={{ color: 'white', fontFamily: 'Outfit, sans-serif' }}>
                  You're earning{' '}
                  <strong
                    style={{
                      background: `linear-gradient(90deg, ${GATE_BLUE}, ${GATE_GREEN})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    2x points
                  </strong>{' '}
                  on every chest opening!
                </p>

                {/* Progress Bar */}
                <div>
                  <div className="flex justify-between text-xs mb-1" style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>
                    <span>Bonus Progress</span>
                    <span style={{ color: GATE_GREEN, fontWeight: 600 }}>{progress} pts</span>
                  </div>
                  <div className="w-full h-2 rounded-full" style={{ backgroundColor: 'rgba(23, 230, 161, 0.2)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.min(progressPercent, 100)}%`,
                        background: `linear-gradient(90deg, ${GATE_BLUE}, ${GATE_GREEN})`,
                      }}
                    />
                  </div>
                </div>

                {/* Time Remaining */}
                <div
                  className="flex justify-between items-center text-xs pt-2"
                  style={{ borderTop: `1px solid rgba(23, 230, 161, 0.3)` }}
                >
                  <span style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>Campaign ends in</span>
                  <span style={{ color: GATE_GREEN, fontWeight: 600, fontFamily: 'Outfit, sans-serif' }}>
                    {timeRemaining}
                  </span>
                </div>
              </>
            ) : (
              <>
                {/* For non-eligible users */}
                <p className="text-sm" style={{ color: 'white', fontFamily: 'Outfit, sans-serif' }}>
                  <strong
                    style={{
                      background: `linear-gradient(90deg, ${GATE_BLUE}, ${GATE_GREEN})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    Gate wallet users
                  </strong>{' '}
                  earn 2x points on every chest opening!
                </p>

                {/* Call to Action */}
                <div
                  className="p-3 rounded-lg text-center"
                  style={{
                    background: `linear-gradient(135deg, ${GATE_BLUE} 0%, ${GATE_GREEN} 100%)`,
                  }}
                >
                  <p className="text-xs font-bold" style={{ color: '#FFFFFF', fontFamily: 'Outfit, sans-serif' }}>
                    Use Gate wallet to earn up to 20,000 bonus points!
                  </p>
                </div>

                {/* Time Remaining */}
                <div
                  className="flex justify-between items-center text-xs pt-2"
                  style={{ borderTop: `1px solid rgba(23, 230, 161, 0.3)` }}
                >
                  <span style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>Offer ends in</span>
                  <span style={{ color: GATE_GREEN, fontWeight: 600, fontFamily: 'Outfit, sans-serif' }}>
                    {timeRemaining}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
